<template>
  <div class="page-container">
    <PageHeader title="Cancel Requests" :subtitle="current.hint" icon="mdi-file-cancel-outline" :count="totalData">
      <template v-slot:actions>
        <v-btn depressed class="btn-act btn-act--neutral" @click="refresh">
          <v-icon left small>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </template>
    </PageHeader>

    <!-- Decision tabs -->
    <div class="stage-tabs" role="tablist" aria-label="Cancel requests">
      <button
        v-for="t in tabs"
        :key="t.key"
        type="button"
        role="tab"
        class="stage-tab"
        :class="['stage-tab--' + t.key, { 'stage-tab--active': t.key === view }]"
        :aria-selected="t.key === view ? 'true' : 'false'"
        @click="selectView(t.key)"
      >
        <span class="stage-tab-icon"><v-icon small>{{ t.icon }}</v-icon></span>
        <span class="stage-tab-text">
          <span class="stage-tab-name">{{ t.name }}</span>
          <span class="stage-tab-task">{{ t.sub }}</span>
        </span>
        <span class="stage-tab-count">{{ counts[t.key] == null ? "–" : counts[t.key] }}</span>
      </button>
    </div>

    <div class="surface">
      <div class="filter-bar">
        <v-text-field
          v-model="keyword"
          outlined
          dense
          hide-details
          clearable
          label="Application number"
          prepend-inner-icon="mdi-magnify"
          @keyup.enter="applyFilters"
          @click:clear="clearKeyword"
        ></v-text-field>
      </div>

      <div v-if="loading" class="pa-4">
        <v-skeleton-loader type="table-row@5"></v-skeleton-loader>
      </div>

      <div v-else-if="!list.length" class="empty-state">
        <v-icon size="52">{{ current.icon }}</v-icon>
        <div class="cell-strong">{{ current.empty }}</div>
        <div>{{ current.emptyText }}</div>
      </div>

      <div v-else class="table-scroll">
        <v-simple-table class="app-table">
          <thead>
            <tr>
              <th>Application</th>
              <th>Applicant</th>
              <th>Shooting</th>
              <th>Reason</th>
              <th>Decision</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in list" :key="item._id">
              <td data-label="Application">
                <div>
                  <span class="app-no">#{{ item.applicationNo || "N/A" }}</span>
                  <div v-if="item.isExtended && item.originalApplication && item.originalApplication.applicationNo" class="cell-muted">
                    <v-icon x-small>mdi-link-variant</v-icon>
                    Extension of #{{ item.originalApplication.applicationNo }}
                  </div>
                  <div class="cell-muted">Submitted {{ formatDate(item.created_at) }}</div>
                </div>
              </td>
              <td data-label="Applicant">
                <div>
                  <div class="cell-strong">{{ item.applicantName || "N/A" }}</div>
                  <div v-if="item.applicantMobileNo" class="cell-muted">{{ item.applicantMobileNo }}</div>
                  <div v-if="item.applicantDistrict" class="cell-muted">{{ item.applicantDistrict }}</div>
                </div>
              </td>
              <td data-label="Shooting">
                <div>
                  <div class="cell-strong">{{ filmName(item) }}</div>
                  <div class="cell-muted">{{ item.natureOfApplication }}</div>
                  <div v-if="item.paymentStatus === 'Success'" class="paid-chip">
                    <v-icon x-small>mdi-cash-check</v-icon>
                    Paid ₹{{ money(paidTotal(item)) }}
                  </div>
                </div>
              </td>
              <td data-label="Reason">
                <div class="reason-cell">
                  <div class="reason-text" :title="reasonOf(item)">{{ reasonOf(item) || "—" }}</div>
                  <div v-if="item.cancellation && item.cancellation.requestedAt" class="cell-muted">
                    Requested {{ formatDate(item.cancellation.requestedAt) }} · {{ fromNow(item.cancellation.requestedAt) }}
                  </div>
                </div>
              </td>
              <td data-label="Decision">
                <div class="status-stack">
                  <span class="status-pill" :class="'status-pill--' + decisionOf(item).tone">{{ decisionOf(item).label }}</span>
                  <div v-if="item.cancellation && item.cancellation.decidedAt" class="cell-muted">{{ formatDate(item.cancellation.decidedAt) }}</div>
                  <div v-if="view === 'denied'" class="cell-muted">Now: {{ statusOf(item).label }}</div>
                  <div v-if="view === 'accepted' && item.paymentStatus === 'Success'" class="cell-muted">
                    Refund: {{ { Pending: "to be raised", Requested: "requested", Completed: "completed", Failed: "failed" }[item.refundStatus] || "to be raised" }}
                  </div>
                  <div v-if="item.cancellation && item.cancellation.remarks" class="decision-remarks" :title="item.cancellation.remarks">
                    “{{ item.cancellation.remarks }}”
                  </div>
                </div>
              </td>
              <td class="app-table-actions text-right">
                <div class="row-actions">
                  <v-btn
                    depressed
                    small
                    class="btn-act btn-act--sm"
                    :class="view === 'pending' ? 'btn-act--amber' : 'btn-act--primary'"
                    @click="$router.push('/FilmDetailedView?id=' + item._id)"
                  >
                    <v-icon left small>{{ view === "pending" ? "mdi-gavel" : "mdi-eye-outline" }}</v-icon>
                    {{ view === "pending" ? "Review" : "Open" }}
                  </v-btn>
                  <v-btn depressed small class="btn-act btn-act--sm btn-act--info" @click="openTimeline(item)">
                    <v-icon left small>mdi-timeline-clock-outline</v-icon>
                    Timeline
                  </v-btn>
                </div>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>

      <div v-if="pages > 1" class="pagination-bar">
        <v-pagination v-model="currentPage" :length="pages" :total-visible="7" color="#15803d"></v-pagination>
      </div>
    </div>

    <TimelineDialog v-model="timelineDialog" :application="selectedApp || {}" />
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import PageHeader from "@/components/Common/PageHeader.vue";
import TimelineDialog from "@/components/Common/TimelineDialog.vue";
import { statusOf } from "@/components/Common/deskConfig";

const PAGE_SIZE = 10;

const TABS = [
  {
    key: "pending",
    name: "Awaiting decision",
    sub: "Requests to review",
    icon: "mdi-timer-sand",
    hint: "Applicants who asked to cancel. Open one to accept or deny it.",
    empty: "No pending requests",
    emptyText: "New cancellation requests from applicants will appear here.",
    decision: { label: "Awaiting your decision", tone: "warning" },
  },
  {
    key: "accepted",
    name: "Accepted",
    sub: "Applications cancelled",
    icon: "mdi-cancel",
    hint: "Requests you accepted. These applications are closed as cancelled.",
    empty: "Nothing accepted yet",
    emptyText: "Applications cancelled on request will appear here.",
    decision: { label: "Cancellation accepted", tone: "danger" },
  },
  {
    key: "denied",
    name: "Denied",
    sub: "Applications continue",
    icon: "mdi-undo-variant",
    hint: "Requests you turned down. These applications stayed active.",
    empty: "Nothing denied yet",
    emptyText: "Requests you deny will appear here.",
    decision: { label: "Cancellation denied", tone: "success" },
  },
];

// DFO's cancellation requests, split by decision: awaiting, accepted, denied.
// The tab is kept in the address (?view=).
export default {
  name: "CancelRequests",
  components: { PageHeader, TimelineDialog },
  data() {
    const view = this.$route.query.view;
    return {
      tabs: TABS,
      view: TABS.some((t) => t.key === view) ? view : "pending",
      list: [],
      counts: {},
      loading: false,
      keyword: "",
      currentPage: 1,
      totalData: 0,
      pages: 0,
      timelineDialog: false,
      selectedApp: null,
    };
  },
  computed: {
    current() {
      return TABS.find((t) => t.key === this.view) || TABS[0];
    },
  },
  watch: {
    currentPage() {
      this.getList();
    },
  },
  mounted() {
    this.getList();
  },
  methods: {
    statusOf,
    selectView(key) {
      if (key === this.view) return;
      this.view = key;
      this.$router.replace({ query: { ...this.$route.query, view: key } }).catch(() => {});
      this.applyFilters();
    },
    applyFilters() {
      if (this.currentPage !== 1) this.currentPage = 1;
      else this.getList();
    },
    clearKeyword() {
      this.keyword = "";
      this.applyFilters();
    },
    refresh() {
      this.getList();
    },
    getList() {
      this.loading = true;
      axios({
        method: "POST",
        url: "/division/cancel/requests",
        headers: { token: localStorage.getItem("token") },
        data: {
          view: this.view,
          page: this.currentPage,
          limit: PAGE_SIZE,
          applicationNo: this.keyword || "",
        },
      })
        .then((response) => {
          this.loading = false;
          const res = response.data || {};
          if (!res.status) {
            this.list = [];
            this.totalData = 0;
            this.pages = 0;
            return;
          }
          this.list = res.data || [];
          this.counts = res.counts || {};
          this.totalData = res.totalLength || 0;
          this.pages = Math.ceil(this.totalData / PAGE_SIZE);
        })
        .catch((err) => {
          this.loading = false;
          console.error(err);
        });
    },
    decisionOf() {
      return this.current.decision;
    },
    reasonOf(item) {
      return item.reason && item.reason !== "Nill" ? item.reason : "";
    },
    paidTotal(item) {
      return Number(item.shootingfee || 0) + Number(item.installationfee || 0) + Number(item.securitydeposit || 0);
    },
    openTimeline(item) {
      this.selectedApp = item;
      this.timelineDialog = true;
    },
    filmName(item) {
      const first = item.filmShootingDetails && item.filmShootingDetails[0];
      return (first && first.filmName) || "Untitled";
    },
    formatDate(value) {
      return value ? moment(value).format("DD MMM YYYY") : "—";
    },
    fromNow(value) {
      return value ? moment(value).fromNow() : "";
    },
    money(value) {
      return Number(value || 0).toLocaleString("en-IN");
    },
  },
};
</script>

<style scoped>
.reason-cell {
  max-width: 280px;
}

.reason-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--ink);
}

.decision-remarks {
  max-width: 220px;
  margin-top: 2px;
  font-size: 12.5px;
  font-style: italic;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.paid-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #dcfce7;
  color: #15803d;
  font-size: 11.5px;
  font-weight: 700;
}

.paid-chip .v-icon {
  color: inherit !important;
}

/* Tabs — same cards as the desk list */
.stage-tabs {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.stage-tab {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 14px 16px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: #ffffff;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.stage-tab:hover {
  border-color: rgba(21, 128, 61, 0.35);
  transform: translateY(-1px);
}

.stage-tab--active {
  border-color: var(--brand);
  background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%);
  box-shadow: 0 8px 22px rgba(21, 128, 61, 0.14);
}

.stage-tab-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 12px;
  background: var(--brand-soft);
}

.stage-tab-icon .v-icon {
  color: var(--brand) !important;
}

.stage-tab--pending .stage-tab-icon {
  background: #fef3c7;
}

.stage-tab--pending .stage-tab-icon .v-icon {
  color: #b45309 !important;
}

.stage-tab--accepted .stage-tab-icon {
  background: #fee2e2;
}

.stage-tab--accepted .stage-tab-icon .v-icon {
  color: #b91c1c !important;
}

.stage-tab--pending.stage-tab--active .stage-tab-icon {
  background: #b45309;
}

.stage-tab--accepted.stage-tab--active .stage-tab-icon {
  background: #b91c1c;
}

.stage-tab--active .stage-tab-icon {
  background: var(--brand);
}

.stage-tab--active .stage-tab-icon .v-icon {
  color: #ffffff !important;
}

.stage-tab-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.stage-tab-name {
  font-size: 14px;
  font-weight: 800;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stage-tab-task {
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-muted);
}

.stage-tab-count {
  min-width: 34px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #334155;
  font-size: 14px;
  font-weight: 800;
  text-align: center;
}

.stage-tab--active .stage-tab-count {
  background: var(--brand);
  color: #ffffff;
}

@media (max-width: 760px) {
  .stage-tabs {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .reason-cell,
  .decision-remarks {
    max-width: none;
  }
}
</style>
